import { create } from "zustand";
import { persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import {
  AssistantPanelView,
  AssistantPanelVariant,
  AssistantPanelFloatingPosition,
  AssistantPanelFloatingSize,
} from "./types";
import {
  SIDEBAR_DEFAULT_WIDTH,
  SIDEBAR_MIN_WIDTH,
  SIDEBAR_MAX_WIDTH,
  FLOATING_DEFAULT_WIDTH,
  FLOATING_DEFAULT_HEIGHT,
  FLOATING_MIN_WIDTH,
  FLOATING_MIN_HEIGHT,
} from "./constants";
import { ASSISTANT_PANEL_DEFAULT_VIEW } from "../constants";

const getDefaultFloatingPosition = (): AssistantPanelFloatingPosition => ({
  x: Math.max(0, (window.innerWidth - FLOATING_DEFAULT_WIDTH) / 2),
  y: Math.max(0, (window.innerHeight - FLOATING_DEFAULT_HEIGHT) / 2),
});

const getDefaultFloatingSize = (): AssistantPanelFloatingSize => ({
  width: FLOATING_DEFAULT_WIDTH,
  height: FLOATING_DEFAULT_HEIGHT,
});

type AssistantPanelStoreState = {
  isOpen: boolean;
  preferredVariant: AssistantPanelVariant | undefined;
  variant: AssistantPanelVariant;
  width: number;
  floatingPosition: AssistantPanelFloatingPosition;
  floatingSize: AssistantPanelFloatingSize;
  isSidebarResizing: boolean;
  view: AssistantPanelView;
};

type AssistantPanelStoreActions = {
  setIsOpen: (open: boolean) => void;
  toggle: () => void;
  setWidth: (width: number) => void;
  setFloatingPosition: (position: AssistantPanelFloatingPosition) => void;
  setFloatingSize: (size: AssistantPanelFloatingSize) => void;
  setIsSidebarResizing: (isSidebarResizing: boolean) => void;
  pin: () => void;
  unpin: () => void;
  toggleFullscreen: () => void;
  exitFullscreen: () => void;
  clampFloatingToViewport: () => void;
  switchView: (view: AssistantPanelView) => void;
  initialize: (isSmallScreen: boolean) => void;
  onScreenBreakpointChange: (isSmallScreen: boolean) => void;
};

type AssistantPanelStore = AssistantPanelStoreState & AssistantPanelStoreActions;

type AssistantPanelPersistedState = Pick<
  AssistantPanelStoreState,
  "isOpen" | "variant" | "preferredVariant" | "width" | "floatingPosition" | "floatingSize" | "view"
>;

const useAssistantPanelStore = create<AssistantPanelStore>()(
  persist(
    immer((set, get) => ({
      isOpen: false,
      preferredVariant: undefined,
      variant: "side",
      width: SIDEBAR_DEFAULT_WIDTH,
      floatingPosition: getDefaultFloatingPosition(),
      floatingSize: getDefaultFloatingSize(),
      isSidebarResizing: false,
      view: ASSISTANT_PANEL_DEFAULT_VIEW,
      setIsOpen: (open) => {
        set((state) => {
          state.isOpen = open;
        });
      },

      toggle: () => {
        set((state) => {
          state.isOpen = !state.isOpen;
        });
      },

      setWidth: (width) => {
        set((state) => {
          state.width = Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, width));
        });
      },

      setFloatingPosition: (position) => {
        set((state) => {
          state.floatingPosition = {
            x: Math.max(0, Math.min(window.innerWidth - state.floatingSize.width, position.x)),
            y: Math.max(0, Math.min(window.innerHeight - state.floatingSize.height, position.y)),
          };
        });
      },

      setFloatingSize: (size) => {
        set((state) => {
          state.floatingSize = {
            width: Math.max(FLOATING_MIN_WIDTH, Math.min(window.innerWidth, size.width)),
            height: Math.max(FLOATING_MIN_HEIGHT, Math.min(window.innerHeight, size.height)),
          };
        });
      },

      setIsSidebarResizing: (isResizing) => {
        set((state) => {
          state.isSidebarResizing = isResizing;
        });
      },

      pin: () => {
        set((state) => {
          state.preferredVariant = "side";
          state.variant = state.preferredVariant;
        });
      },

      unpin: () => {
        set((state) => {
          state.preferredVariant = "floating";
          state.variant = state.preferredVariant;
        });
        get().clampFloatingToViewport();
      },

      toggleFullscreen: () => {
        set((state) => {
          state.variant = state.variant === "fullscreen" ? "floating" : "fullscreen";
        });
      },

      exitFullscreen: () => {
        set((state) => {
          state.variant = "floating";
          state.floatingSize = { width: window.innerWidth, height: window.innerHeight };
          state.floatingPosition = { x: 0, y: 0 };
        });
      },

      clampFloatingToViewport: () => {
        set((state) => {
          const width = Math.max(
            FLOATING_MIN_WIDTH,
            Math.min(window.innerWidth, state.floatingSize.width)
          );
          const height = Math.max(
            FLOATING_MIN_HEIGHT,
            Math.min(window.innerHeight, state.floatingSize.height)
          );

          state.floatingSize = { width, height };
          state.floatingPosition = {
            x: Math.max(0, Math.min(window.innerWidth - width, state.floatingPosition.x)),
            y: Math.max(0, Math.min(window.innerHeight - height, state.floatingPosition.y)),
          };
        });
      },

      switchView: (view) => {
        set((state) => {
          state.view = view;
        });
      },

      initialize: (isSmallScreen: boolean) => {
        set((state) => {
          if (!state.preferredVariant) {
            state.variant = isSmallScreen ? "floating" : "side";
          }
        });
      },

      onScreenBreakpointChange: (isSmallScreen: boolean) => {
        const wasInSideMode = get().variant === "side";
        set((state) => {
          // Fullscreen fills the viewport at any size, so it survives breakpoint changes
          if (state.variant === "fullscreen") {
            return;
          }
          if (isSmallScreen) {
            state.variant = "floating";
          } else {
            state.variant = state.preferredVariant ?? "side";
          }
        });

        if (isSmallScreen && wasInSideMode) {
          get().clampFloatingToViewport();
        }
      },
    })),
    {
      name: "assistant-panel-layout-store",
      version: 5,
      partialize: (state): AssistantPanelPersistedState => ({
        isOpen: state.isOpen,
        variant: state.variant,
        preferredVariant: state.preferredVariant,
        width: state.width,
        floatingPosition: state.floatingPosition,
        floatingSize: state.floatingSize,
        view: state.view,
      }),
      migrate: (persistedState, version): AssistantPanelPersistedState => {
        const { isFullscreen, ...persisted } = persistedState as AssistantPanelPersistedState & {
          isFullscreen?: boolean;
        };

        if (version === 4 && isFullscreen) {
          return { ...persisted, variant: "fullscreen" };
        }

        return persisted;
      },
    }
  )
);

export default useAssistantPanelStore;
